// ----------------------------------------------------------------------
// <copyright file="Izenda.CategoryDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var objectState = require('logic/data/Izenda.ObjectStateConstants');
  var DashboardConstants = require('logic/data/Izenda.DashboardConstants');
  var StaticDashboardDetail = require('logic/models/Dashboard/Izenda.StaticDashboardDetail');
  var WorkspaceDashboardDetail = require('logic/models/Izenda.WorkspaceDashboardDetail');
  var ReportDetail = require('logic/models/Izenda.ReportDetail');
  var uiState = require('logic/Izenda.UiState.js');
  var CONSTANT = require('logic/data/Izenda.ReportsConstants');

  const { forEach, map, isNil } = require('lodash/fp');
  function CategoryDetail(data, hasGetDashboardParts) {
    var props = {
      id: '',
      type: CONSTANT.CATEGORY_TYPE.REPORT,
      name: '',
      parentId: '',
      dateTimeNow: '',
      status: objectState.NONE,
      state: objectState.NONE,
      modified: null,
      canDelete: true,
      subCategories: [],
      dashboards: [],
      reports: [],
      checked: false,
      indeterminate: false,
      numOfCheckedChilds: 0,
      numOfChilds: 0,
      isGlobal: false,
      isEditing: false, // used for one-instance editing purpose
      editable: true,

      tenantId: uiState.tenantId,

      isUncategorized: function() {
        return isNil(this.id) || this.id === CONSTANT.EMPTY_GUID;
      },

      isSubCategory: function() {
        return !isNil(this.parentId);
      },

      isRootCategory: function() {
        return this.id === DashboardConstants.ALL_CATEGORY_TYPES.ALL_DASHBOARDS.id;
      },

      isUneditable: function() {
        return this.isRootCategory || this.id === CONSTANT.EMPTY_GUID || isNil(this.id) || !this.editable;
      },

      isEmpty: function() {
        return this.canDelete;
      }
    };

    m.extendObservable(this, props);
    data && this.populateData(data, hasGetDashboardParts);
  }

  /**
   * Populate data
   * @param  {Object} category detail data
   */
  CategoryDetail.prototype.populateData = m.action(function(data, hasGetDashboardParts) {
    data = data || {};
    this.id = data.id;
    this.type = data.type;
    this.name = data.name;
    this.parentId = data.parentId;
    this.tenantId = data.tenantId;
    this.dateTimeNow = data.dateTimeNow;
    this.status = data.status;
    this.state = data.state;
    this.modified = data.modified;
    this.subCategories = data.subCategories;
    this.canDelete = data.canDelete;
    this.editable = data.editable;
    this.checked = data.checked;
    this.numOfCheckedChilds = data.numOfCheckedChilds;
    this.numOfChilds = data.numOfChilds;
    this.isGlobal = data.isGlobal;

    this.populateSubCategories(data.subCategories, hasGetDashboardParts);
    this.populateDashboardData(data.dashboards, hasGetDashboardParts);
    this.populateReportData(data.reports);
  });

  /**
   * Populate data for global/local categories
   * @param  {Object} data categories detail data
   */
  CategoryDetail.prototype.populateDataForGlobalLocalCategory = m.action(function(data) {
    data = data || {};

    this.id = data.id;
    this.type = data.type;
    this.name = data.name;
    this.parentId = data.parentId;
    this.tenantId = data.tenantId;
    this.dateTimeNow = data.dateTimeNow;
    this.status = data.status;
    this.state = data.state;
    this.modified = data.modified;
    this.subCategories = data.subCategories;
    this.canDelete = data.canDelete;
    this.editable = data.editable;
    this.checked = data.checked;
    this.numOfCheckedChilds = data.numOfCheckedChilds;
    this.numOfChilds = data.numOfChilds;
    this.isGlobal = data.isGlobal;
    this.subCategories = map(function(subCategory) {
      return new CategoryDetail(subCategory);
    }, data.subCategories);
  });

  /**
   * Populate sub categories
   * @param  {Object} sub category data
   */
  CategoryDetail.prototype.populateSubCategories = m.action(function(data, hasGetDashboardParts) {
    data = data || [];
    this.subCategories = data.map(function(item) {
      return new CategoryDetail(item, hasGetDashboardParts);
    });
  });

  /**
   * update category name
   * @param {String} name
   */
  CategoryDetail.prototype.updateName = function(value) {
    this.name = value;
  };

  /**
   * update isEditing
   * @param {Boolean} isEditing
   */
  CategoryDetail.prototype.updateIsEditing = function(isEditing) {
    this.isEditing = isEditing;
  };

  /**
   * Populate dashboard items
   * @param  {Array} data
   */
  CategoryDetail.prototype.populateDashboardData = function(data, hasGetDashboardParts) {
    data = data || [];
    this.dashboards = data.map(function(item) {
      return hasGetDashboardParts ? new WorkspaceDashboardDetail(item) : new StaticDashboardDetail(item);
    });
  };

  /**
   * Populate report items
   * @param  {Array} data
   */
  CategoryDetail.prototype.populateReportData = function(data) {
    data = data || [];
    this.reports = data.map(function(item) {
      return new ReportDetail(item);
    });
  };

  /**
   * Reset subscribe all reports
   */
  CategoryDetail.prototype.resetSubscribes = m.action(function() {
    forEach(function(item) {
      item.isSubscribeMode = false;
    }, this.reports);

    forEach(function(subCategory) {
      forEach(function(item) {
        item.isSubscribeMode = false;
      }, subCategory.reports);
    }, this.subCategories);
  });

  module.exports = CategoryDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.CategoryDetail.js